/* eslint-disable import/no-unresolved, import/no-extraneous-dependencies, no-nested-ternary */
import React from 'react';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Pagination from '@mui/material/Pagination';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import { useQuery } from '@tanstack/react-query';
import { NavLink, useSearchParams } from 'react-router-dom';
import {
  OmniverseHeader,
  LoaderError,
  SessionDuration,
  SessionStatus,
  SessionStatusFilter,
  SessionTerminateButton,
} from '../components';
import { useOmniverseConfig } from '../hooks/useConfig';
import { getSessions, StreamingSession } from '../state/Sessions';

/**
 * The web page to see a list of streaming sessions.
 * Can filter sessions based on the current status (active, idle, stopped, connecting).
 * System administrators can see all sessions started by other users.
 *
 * For each active session displays a terminate button that allows users to
 * forcefully close the user connection.
 */
export default function UserSessionList() {
  const config = useOmniverseConfig();

  const [params, setParams] = useSearchParams();
  const page = params.has('page') ? Number(params.get('page')) : 1;

  function updatePage(newPage: number) {
    params.set('page', newPage.toString());
    setParams(params);
  }

  const status = params.get('status') ?? '';

  function updateStatus(newStatus: string | null) {
    if (newStatus) {
      params.set('status', newStatus);
    } else {
      params.delete('status');
    }
    setParams(params);
  }

  const { isLoading, error, data } = useQuery({
    queryKey: ['sessions', page, status],
    queryFn: async () => getSessions({ config, page, status }),
  });

  return (
    <Box>
      <OmniverseHeader />

      <Box sx={{ px: 4, py: 3 }}>
        <Typography variant="h5" color="text.secondary" sx={{ mb: 2 }}>
          Sessions
        </Typography>

        {/* <Breadcrumbs sx={{ mb: 3 }}>
          <Link component={NavLink} to="/omniverse" underline="hover" color="primary">
            Main page
          </Link>
          <Link component={NavLink} to="/omniverse/sessions" underline="hover" color="primary">
            Sessions
          </Link>
        </Breadcrumbs> */}

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <LoaderError title={'Failed to load streaming applications'}>
            {error.toString()}
          </LoaderError>
        ) : (
          data && (
            <>
              <Box sx={{ py: 2, pt: 0, display: 'flex', justifyContent: 'flex-end' }}>
                <SessionStatusFilter
                  value={status as StreamingSession['status']}
                  onChange={updateStatus}
                />
              </Box>

              <TableContainer
                component={Paper}
                sx={{
                  boxShadow: '3px 3px 5px #E8E8E8',
                  border: '0.1rem solid #E8E8E8',
                  mb: 3,
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ maxWidth: 300, fontWeight: 600, fontSize: '1.575rem' }}>#</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '1.575rem' }}>App</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '1.575rem' }}>User</TableCell>
                      <TableCell sx={{ width: 125, fontWeight: 600, fontSize: '1.575rem' }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '1.575rem' }}>Start date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '1.575rem' }}>End date</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '1.575rem' }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 600, fontSize: '1.575rem' }}></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data?.items.map((session) => (
                      <TableRow
                        key={session.id}
                        sx={{
                          height: 50,
                          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                        }}
                      >
                        <TableCell sx={{ fontSize: '1.53rem', maxWidth: 300 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 300,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                              fontSize: '1.53rem',
                            }}
                            title={session.id}
                          >
                            {session.id}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '1.53rem' }}>
                          {session.app ? (
                            <Link
                              component={NavLink}
                              to={`/omniverse/app/${session.app.id}`}
                              underline="hover"
                              color="primary"
                              sx={{ fontSize: '1.53rem' }}
                            >
                              {session.app.productArea} {session.app.title}{' '}
                              {session.app.version}
                            </Link>
                          ) : (
                            '(unknown)'
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: '1.53rem' }}>
                          {session.userName}
                        </TableCell>
                        <TableCell>
                          <SessionStatus status={session.status} />
                        </TableCell>
                        <TableCell sx={{ fontSize: '1.53rem' }}>
                          {session.startDate.toLocaleString()}
                        </TableCell>
                        <TableCell sx={{ fontSize: '1.53rem' }}>
                          {session.endDate
                            ? session.endDate.toLocaleString()
                            : ''}
                        </TableCell>
                        <TableCell sx={{ fontSize: '1.53rem' }}>
                          <SessionDuration session={session} />
                        </TableCell>
                        <TableCell>
                          {session.status !== 'STOPPED' && (
                            <SessionTerminateButton session={session} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Pagination
                  count={data.totalPages}
                  page={data.page}
                  showFirstButton
                  showLastButton
                  onChange={(_, newPage) => updatePage(newPage)}
                  color="primary"
                />
              </Box>
            </>
          )
        )}
      </Box>
    </Box>
  );
}
